/**
 * Plain words for the audit log (TRA-1626): what each recorded action is
 * called on the page, who the actor was, and the before/after field list
 * a row's detail sheet shows. Pure, so the section test can read them.
 *
 * An action label says what happened, never who did it: the line under
 * the actor (`actorNote`) already says when a row is TrainLoop staff's.
 */

import type { AuditEvent } from '@/hooks/useAuditEvents'

/** What the page calls each action the API records. An action not listed
    here (a later slice's) shows as its own name. */
export const ACTION_LABELS: Record<string, string> = {
  'trace.view': 'Opened a trace',
  'trajectory.view': 'Opened a trajectory',
  'conversation.view': 'Opened a conversation',
  'review.queue.view': 'Opened the review queue',
  'review.queue.models.view': 'Loaded the review queue models',
  'review.queue.metadata.view': 'Loaded the review queue metadata',
  'review.chat': 'Asked about a run',
  'review.save': 'Saved a review',
  'review.dismiss': 'Dismissed a flag',
  'review.remove_flag': 'Removed a flag',
  'request_log.view': 'Opened the request log',
  'trajectory_list.view': 'Opened the trajectories list',
  'conversation_list.view': 'Opened the conversations list',
  'label_list.view': 'Opened the reviews list',
  'label.view': 'Viewed a review',
  'rollout.view': 'Opened a rollout',
  'rollout_list.view': 'Opened the rollouts of a task',
  'task_process.view': 'Opened the golden process of a task',
  'eval_run.view': 'Opened an eval run',
  'refinery.view': 'Opened the Data refinery',
  'refinery_tree.view': 'Opened the data refinery tree',
  'proven_task.view': 'Opened a generated task',
  'proven_task_file.view': 'Opened a generated task file',
  'dataset.view': 'Opened a dataset',
  'dataset.items.view': 'Viewed the items of a dataset',
  'billing.view': 'Viewed billing',
  'invoice_list.view': 'Viewed invoices',
  'spend.view': 'Viewed spend',
  'member_list.view': 'Viewed the member list',
  'notification_settings.view': 'Viewed the notification settings',
  'failure_record.view': 'Opened a failure record',
  'failure_transcript.view': 'Opened a failure transcript',
  'training_agent_session.view': 'Opened a training agent session',
  'prompt.save': 'Saved a prompt',
  'prompt.activate': 'Activated a prompt version',
  'model.update': 'Changed a model',
  'model.delete': 'Deleted a model',
  'model.alias.add': 'Added a model alias',
  'model.alias.remove': 'Removed a model alias',
  'model.alias.promote': 'Promoted a model alias',
  'model.set_default': 'Set the default model',
  'member.invite': 'Invited a member',
  'member.remove': 'Removed a member',
  'member.leave': 'Left the team',
  'member.permissions': "Changed a member's permissions",
  'team.transfer_ownership': 'Transferred ownership',
  'api_key.create': 'Created an API key',
  'api_key.revoke': 'Revoked an API key',
  'audit_log.view': 'Viewed the audit log',
  'audit_log.export': 'Exported the audit log',
  'impersonation.request': 'Viewed the team (read-only)',
  'staff.view': 'Viewed team configuration',
  'staff.change': 'Changed team configuration',
}

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action
}

/** Who did it, as the row names them. */
export function actorLabel(event: AuditEvent): string {
  if (event.actor_type === 'api_key') return event.actor_id ?? 'API key'
  if (event.actor_type === 'system') return event.actor_id ?? 'TrainLoop system'
  if (event.actor_type === 'anonymous') return 'Not signed in'
  return event.actor_email ?? event.actor_id ?? 'Unknown'
}

export type ActorNote = 'TrainLoop staff' | 'TrainLoop system' | 'API key' | null

/** The muted line under the actor. A TrainLoop employee is marked on
    every row, whether they acted as staff or as a member of the team
    (the API says so from their email: `is_trainloop_staff`). Our jobs
    and API keys are named for what they are. A customer gets no line. */
export function actorNote(event: AuditEvent): ActorNote {
  if (event.is_trainloop_staff) return 'TrainLoop staff'
  if (event.actor_type === 'system') return 'TrainLoop system'
  if (event.actor_type === 'api_key') return 'API key'
  return null
}

export const OUTCOME_LABELS: Record<AuditEvent['outcome'], string> = {
  ok: 'OK',
  denied: 'Denied',
  error: 'Error',
}

export function outcomeVariant(outcome: AuditEvent['outcome']): 'success' | 'destructive' | 'warning' {
  if (outcome === 'ok') return 'success'
  if (outcome === 'denied') return 'warning'
  return 'destructive'
}

/** The resource as one line: its kind and its id, when the row has them. */
export function resourceLabel(event: AuditEvent): string {
  if (event.resource_type === null && event.resource_id === null) return ''
  if (event.resource_id === null) return event.resource_type ?? ''
  if (event.resource_type === null) return event.resource_id
  return `${event.resource_type} ${event.resource_id}`
}

/** A JSON value as the sheet prints it. */
export function formatValue(value: unknown): string {
  if (value === undefined || value === null) return 'empty'
  if (typeof value === 'string') return value === '' ? 'empty' : value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value, null, 2)
}

export interface FieldRow {
  field: string
  before: unknown
  after: unknown
  changed: boolean
}

/** Every field of the two states as one list, the changed ones first. */
export function fieldRows(event: Pick<AuditEvent, 'before' | 'after' | 'changed_fields'>): FieldRow[] {
  const before = event.before ?? {}
  const after = event.after ?? {}
  const names = [...Object.keys(before), ...Object.keys(after).filter((k) => !(k in before))]
  const changed = new Set(event.changed_fields)
  const rows = names.map((field) => ({
    field,
    before: before[field],
    after: after[field],
    changed: changed.has(field),
  }))
  return [...rows.filter((r) => r.changed), ...rows.filter((r) => !r.changed)]
}

/** The time range filter: how far back the page looks. */
export const TIME_RANGES = ['24h', '7d', '30d', 'all'] as const
export type TimeRange = (typeof TIME_RANGES)[number]
export const TIME_RANGE_LABELS: Record<TimeRange, string> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  all: 'All time',
}
const RANGE_HOURS: Record<Exclude<TimeRange, 'all'>, number> = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 }

/** The `from` bound for a range at `now`, or undefined for all time. */
export function rangeStart(range: TimeRange, now: Date): string | undefined {
  if (range === 'all') return undefined
  return new Date(now.getTime() - RANGE_HOURS[range] * 60 * 60 * 1000).toISOString()
}

// Built once; local time with the date, so a row from last month reads right.
const WHEN_FORMAT = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})

/** The event's time in the viewer's local time. */
export function formatWhen(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return WHEN_FORMAT.format(date)
}
